import React, { useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useModal } from '../../../../../hooks/useModal';
import { FormProvider, useForm } from 'react-hook-form';
import { ModalsKeys } from '../../../../../global';
import { useMutation } from '@apollo/client';
import {
  IntegrationSettingsForm,
  IntegrationSettingsLocationLevelTableData,
  IntegrationSettingsTabs,
  IntegrationSettingsUserLevelForm,
} from 'modules/integrationSettings/types';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { UserTab } from './userTab';
import { LocationTab } from './locationTab';
import { AccountTab } from './accountTab';
import { useFetchIntegrationSettingsDialogData } from 'modules/integrationSettings/hooks/useFetchIntegrationSettingsDialogData';
import { formatIntegrationSettingsFormDataToPayload } from 'modules/integrationSettings/lib';
import { useNotifications } from 'modules/notification';
import { INSERT_INTEGRATION_SETTINGS_VALUES_ARRAY } from 'modules/integrationSettings/api';

export const IntegrationSettingsDialog = () => {
  const { isOpen, data, handleClose } = useModal(ModalsKeys.IntegrationSettingsDialog);

  const { showNotification } = useNotifications();

  const { integrationName, dealerAccountId, dealerAccountName, dealerContacts, dealerLocations } = data;

  const tabComponentByLevel: Record<
    IntegrationSettingsTabs,
    React.ComponentType<{ integrationSettings: any; tableData: IntegrationSettingsUserLevelForm | any }>
  > = {
    [IntegrationSettingsTabs.User]: UserTab,
    [IntegrationSettingsTabs.Location]: LocationTab,
    [IntegrationSettingsTabs.Account]: AccountTab,
  };

  const { isFetching, tabs, formData } = useFetchIntegrationSettingsDialogData(
    integrationName,
    dealerAccountId,
    dealerAccountName,
    dealerContacts,
    dealerLocations,
    tabComponentByLevel
  );

  const form = useForm<IntegrationSettingsForm>();

  const [insertIntegrationSettingsValuesArray, { loading: isISVInsertLoading }] = useMutation(
    INSERT_INTEGRATION_SETTINGS_VALUES_ARRAY
  );

  const handleSave = async (formData: IntegrationSettingsForm) => {
    try {
      const input = formatIntegrationSettingsFormDataToPayload(integrationName, formData);

      if (input.length) {
        await insertIntegrationSettingsValuesArray({
          variables: { input },
        });
        showNotification('Changes Saved!');
      }
      handleClose();
    } catch (error) {
      console.error(error);
      showNotification('Error!', { type: 'error' });
    }
  };

  useEffect(() => {
    form.reset(formData);
  }, [formData]);

  const isLoading = isFetching || !tabs.length || isISVInsertLoading;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="lg" fullWidth={true}>
      <DialogTitle>
        <Typography component="span" variant="h5" style={{ fontWeight: 500 }}>
          Integration Settings
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {!isLoading && (
              <FormProvider {...form}>
                <TabsContainer<IntegrationSettingsTabs> tabs={tabs} />
              </FormProvider>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {!isLoading ? (
          <>
            <Button onClick={handleClose} color="primary" variant="outlined">
              Cancel
            </Button>
            <Button onClick={form.handleSubmit(handleSave)} color="primary" variant="contained">
              Save
            </Button>
          </>
        ) : (
          <LinearProgress style={{ width: '100%' }} />
        )}
      </DialogActions>
    </Dialog>
  );
};
